import { useRef, useState } from 'react'
import {
  ChartShell, ChartTooltip, axisFormatter, colorVar, formatChartValue, niceScale, seriesTable, summarize, textWidth, thinIndices, useTweened, useTweenedDomain,
  type ChartSeries, type ChartUnit,
} from './chartKit'

export interface LineChartProps {
  /** Etichette dell'asse (una per punto). */
  categories: string[]
  /** Etichette per esteso nel tooltip (default: le stesse delle categorie). */
  categoryTitles?: string[] | undefined
  series: ChartSeries[]
  /** Area tenue sotto le linee. */
  area?: boolean | undefined
  unit?: ChartUnit | undefined
  height?: number | undefined
  loading?: boolean | undefined
  /** Titolo breve usato nel testo per i lettori di schermo e nella tabella. */
  label: string
  /** Intestazione della prima colonna della tabella (default "Periodo"). */
  categoryHeading?: string | undefined
  emptyTitle?: string | undefined
  emptyDescription?: string | undefined
}

function nearest(e: React.PointerEvent<SVGRectElement>, plotW: number, n: number): number {
  if (n <= 1) return 0
  const x = e.clientX - e.currentTarget.getBoundingClientRect().left
  return Math.max(0, Math.min(n - 1, Math.round(((x - 6) / (plotW - 12)) * (n - 1))))
}

const PAD = { top: 10, right: 12, bottom: 26 }

export function LineChart(p: LineChartProps) {
  const { categories, series, unit = 'number' } = p
  const n = categories.length
  const empty = n === 0 || series.every(s => s.values.every(v => v == null))
  const [active, setActive] = useState<number | null>(null)
  const touch = useRef(false)

  const all = series.flatMap(s => s.values.filter((v): v is number => v != null))
  const scale = niceScale(all.length ? Math.min(...all) : 0, all.length ? Math.max(...all) : 1)
  const fmtAxis = axisFormatter(scale.ticks, unit)
  const keys = series.flatMap(s => categories.map((_, i) => `${s.id}|${i}`))
  const tween = useTweened(keys, series.flatMap(s => categories.map((_, i) => s.values[i] ?? 0)))
  const dom = useTweenedDomain(scale.min, scale.max)

  const height = p.height ?? 240
  const title = (i: number) => p.categoryTitles?.[i] ?? categories[i] ?? ''
  const table = seriesTable(p.label, p.categoryHeading ?? 'Periodo', categories.map((_, i) => title(i)), series, unit)

  return (
    <ChartShell
      loading={p.loading} empty={empty} emptyTitle={p.emptyTitle} emptyDescription={p.emptyDescription}
      ariaLabel={summarize(p.label, categories, series, unit)} table={table} height={height}
      count={n} active={active} onActive={setActive} onLeave={() => { if (!touch.current) setActive(null) }}
      announce={active == null ? '' : `${title(active)}: ${series.map(s => `${s.name} ${formatChartValue(s.values[active] ?? null, unit)}`).join(', ')}`}
    >
      {width => {
        const left = Math.max(...scale.ticks.map(t => textWidth(fmtAxis(t)))) + 8
        const plotW = Math.max(40, width - left - PAD.right)
        const plotH = height - PAD.top - PAD.bottom
        const X = (i: number) => (n === 1 ? left + plotW / 2 : left + 6 + (i * (plotW - 12)) / (n - 1))
        // Scala animata insieme ai valori e limitata all'area del grafico.
        const Y = (v: number) => PAD.top + plotH - ((Math.min(dom.max, Math.max(dom.min, v)) - dom.min) / (dom.max - dom.min)) * plotH
        const y0 = Y(0)
        const shown = thinIndices(n, n > 1 ? (plotW - 12) / (n - 1) : plotW, Math.max(...categories.map(c => textWidth(c))))
        const showDots = n <= 40
        const tooltip = active == null ? null : (
          <ChartTooltip
            x={X(active)} y={Math.min(...series.map(s => (s.values[active] == null ? Infinity : Y(s.values[active]!))), height)} width={width}
            title={title(active)}
            rows={series.map((s, si) => ({ label: s.name, value: formatChartValue(s.values[active] ?? null, unit), color: colorVar(s.color, si) }))}
          />
        )
        return (
          <>
            <svg className="md-chart__svg" width={width} height={height} aria-hidden="true">
              {scale.ticks.filter(t => t >= dom.min - 1e-9 && t <= dom.max + 1e-9).map(t => (
                <g key={t}>
                  <line className={t === 0 ? 'md-chart__axis' : 'md-chart__grid'} x1={left} x2={left + plotW} y1={Y(t)} y2={Y(t)} />
                  <text className="md-chart__text" x={left - 6} y={Y(t)} textAnchor="end" dominantBaseline="central">{fmtAxis(t)}</text>
                </g>
              ))}
              {categories.map((c, i) => shown.has(i) ? (
                <text key={i} className="md-chart__text" x={X(i)} y={height - 8} textAnchor={n > 1 && i === 0 ? 'start' : n > 1 && i === n - 1 ? 'end' : 'middle'}>{c}</text>
              ) : null)}
              {active != null ? <line className="md-chart__guide" x1={X(active)} x2={X(active)} y1={PAD.top} y2={PAD.top + plotH} /> : null}
              {series.map((s, si) => {
                const col = colorVar(s.color, si)
                const base = si * n
                const segs: Array<Array<[number, number]>> = []
                let cur: Array<[number, number]> = []
                s.values.forEach((v, i) => {
                  if (i >= n) return
                  if (v == null) { if (cur.length) segs.push(cur); cur = [] } else cur.push([X(i), Y(tween[base + i] ?? 0)])
                })
                if (cur.length) segs.push(cur)
                return (
                  <g key={s.id}>
                    {p.area ? segs.map((sg, k) => (
                      <path key={k} d={`M${sg[0]![0]},${y0} ${sg.map(pt => `L${pt[0]},${pt[1]}`).join(' ')} L${sg[sg.length - 1]![0]},${y0}Z`} fill={col} fillOpacity={0.12} />
                    )) : null}
                    {segs.map((sg, k) => (
                      <path key={k} d={sg.map((pt, j) => `${j ? 'L' : 'M'}${pt[0]},${pt[1]}`).join(' ')} fill="none" stroke={col} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
                    ))}
                    {s.values.map((v, i) => v != null && i < n && (showDots || i === active) ? (
                      <circle key={i} cx={X(i)} cy={Y(tween[base + i] ?? 0)} r={i === active ? 5 : 3} fill={col} stroke="hsl(var(--md-surface))" strokeWidth={i === active ? 2 : 1} />
                    ) : null)}
                  </g>
                )
              })}
              <rect
                x={left} y={0} width={plotW} height={height - PAD.bottom} fill="transparent"
                onPointerMove={e => { touch.current = e.pointerType === 'touch'; setActive(nearest(e, plotW, n)) }}
                onPointerDown={e => { touch.current = e.pointerType === 'touch'; setActive(nearest(e, plotW, n)) }}
              />
            </svg>
            {tooltip}
          </>
        )
      }}
    </ChartShell>
  )
}
